import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  EnvironmentOutlined,
  GlobalOutlined,
  CopyOutlined,
  DisconnectOutlined,
} from "@/config/antd";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useSelector } from "react-redux";

const LocationMapCard = ({ address, state, virtualLink }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isOnline = useSelector((state) => state.network.isOnline);

  // Combine address and state for full address
  const fullAddress = `${address}, ${state}`;

  // Geocode the address to get coordinates
  useEffect(() => {
    const geocodeAddress = async () => {
      // Skip geocoding if offline
      if (!isOnline) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Create a new Geocoder instance
        const geocoder = new window.google.maps.Geocoder();

        // Geocode the address
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: fullAddress }, (results, status) => {
            if (status === "OK") {
              resolve(results[0]);
            } else {
              reject(status);
            }
          });
        });

        // Set the coordinates from the result
        setCoordinates({
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        });
      } catch (error) {
        console.error("Geocoding error:", error);
        // Set default coordinates (e.g., Lagos)
        setCoordinates({
          lat: 6.5244,
          lng: 3.3792,
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Only attempt geocoding if online and Google Maps is loaded
    if (window.google && fullAddress && isOnline) {
      geocodeAddress();
    } else {
      setIsLoading(false);
    }
  }, [fullAddress, isOnline]);

  // Memoize the map center
  const center = useMemo(
    () => ({
      lat: coordinates?.lat || 6.5244, // Default to Lagos
      lng: coordinates?.lng || 3.3792,
    }),
    [coordinates]
  );

  // Map container style
  const containerStyle = {
    width: "100%",
    height: "300px",
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(virtualLink || "");
    // Optionally add a toast notification here
  };

  return (
    <div className="mt-8 lg:border-2 border-[#eee] rounded-lg">
      <div className="flex justify-between items-center mb-4 p-4">
        <h2 className="text-left heading-medium text-primary">Location Map</h2>
        {isOnline && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              fullAddress
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-small text-darkGrey hover:text-primary-dark">
            OPEN MAP
          </a>
        )}
      </div>

      <div className="bg-white rounded-lg">
        {/* Map Component - show fallback when offline */}
        <div className="w-full h-[300px] bg-gray-100 rounded-lg mb-6">
          {isOnline ? (
            !isLoading &&
            window.google && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={15}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}>
                {coordinates && (
                  <Marker position={coordinates} title={fullAddress} />
                )}
              </GoogleMap>
            )
          ) : (
            <div className="h-full flex flex-col items-center justify-center">
              <DisconnectOutlined className="text-yellow-600 text-2xl mb-2" />
              <p className="text-sm text-gray-600">
                Map unavailable while offline
              </p>
              <p className="text-xs text-gray-500 mt-2">{fullAddress}</p>
            </div>
          )}
        </div>

        {/* Physical Address */}
        <div className="flex items-start gap-3 mb-4 px-4">
          <EnvironmentOutlined className="text-primary mt-1" />
          <div className="flex flex-col items-start flex-grow">
            <p className="font-medium text-left text-darkGrey">{address}</p>
            <p className="text-small text-left text-darkGrey">{state}</p>
          </div>
        </div>

        {/* Virtual Link */}
        {virtualLink && (
          <div className="flex items-start gap-3 pt-4 border-t border-[#eee] px-4 pb-4">
            <GlobalOutlined className="text-primary mt-1" />
            <div className="flex flex-col items-start flex-grow">
              <p className="font-medium text-left text-darkGrey">
                {virtualLink}
              </p>
              <p className="text-small text-left text-darkGrey">
                For Virtual Attendees
              </p>
            </div>
            <button
              className="transition-colors hover:text-primary"
              onClick={handleCopyLink}>
              <CopyOutlined />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

LocationMapCard.propTypes = {
  address: PropTypes.string,
  state: PropTypes.string,
  virtualLink: PropTypes.string,
};

export default LocationMapCard;
